import { Component, EventEmitter, Inject, OnInit, Optional, Output } from '@angular/core'
import { FormBuilder, FormGroup, ReactiveFormsModule } from '@angular/forms'
import { MatButtonModule } from '@angular/material/button'
import { MatFormField, MatFormFieldModule, MatLabel } from '@angular/material/form-field'
import { MatInput, MatInputModule } from '@angular/material/input'
import { MatSelectModule } from '@angular/material/select'
import { MatCheckboxModule } from '@angular/material/checkbox'
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog'
import { MatIconModule } from '@angular/material/icon'
import { MatDatepicker, MatDatepickerModule } from '@angular/material/datepicker'
import { MatNativeDateModule, MatOption } from '@angular/material/core'
import { CommonModule } from '@angular/common'
import { MatTooltip } from '@angular/material/tooltip'

import { SupplierService } from '../../../core/services/person/supplier/supplier.service'
import { ModalLegalEntityClientComponent } from '../../../features/customer/modal_client/modal-legal-entity-client/modal-legal-entity-client.component'
import { ModalEmployeeConsultantOperationalComponent } from '../../../features/employee/modal-employee-consultant-operational/modal-employee-consultant-operational.component'

// Modal de filtros avançados da Torre (aberto via botão "Filtros avançados" na
// página, não mais embutido — muita informação na tela de uma vez atrapalhava a
// leitura). A busca livre (TG/OS/agência/cliente final/motorista/passageiro) e o
// filtro rápido de cor ficam na barra do topo da página, não aqui — este modal só
// carrega o que POST Monitoramento/search-paginated aceita além disso: período,
// fornecedor/tipo, cliente, consultor operacional, sem motorista, com ocorrência.
@Component({
  selector: 'app-monitoring-os-search',
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatFormField,
    MatInput,
    MatLabel,
    MatSelectModule,
    MatCheckboxModule,
    MatIconModule,
    MatDatepicker,
    MatFormFieldModule,
    MatInputModule,
    MatDatepickerModule,
    MatNativeDateModule,
    MatOption,
    MatButtonModule,
    MatTooltip,
  ],
  templateUrl: './monitoring-os-search.component.html',
  styleUrl: './monitoring-os-search.component.scss',
  standalone: true,
})
export class MonitoringOsSearchComponent implements OnInit {
  constructor(
    private formBuilder: FormBuilder,
    private supplierService: SupplierService,
    private dialog: MatDialog,
    private dialogRef: MatDialogRef<MonitoringOsSearchComponent>,
    // Opcional — dialog.open() cria uma instância nova a cada abertura, então sem
    // isto o form sempre nascia vazio mesmo depois de já ter sido usado (a página
    // guardava o filtro pra buscar, mas nunca devolvia pro modal). Ver `raw` em search().
    @Optional() @Inject(MAT_DIALOG_DATA) private data: { form?: any; clientSelected?: any; consultorSelected?: any } | null,
  ) {}

  form!: FormGroup
  listFornecedores: any[] = []
  clientSelected: any = {}
  consultorSelected: any = {}

  @Output() response = new EventEmitter<any>()

  ngOnInit(): void {
    this.createForm()
    this.getListFornecedores()
    if (this.data?.form) {
      this.form.patchValue(this.data.form)
      this.clientSelected = this.data.clientSelected ?? {}
      this.consultorSelected = this.data.consultorSelected ?? {}
    }
  }

  createForm(): void {
    this.form = this.formBuilder.group({
      dateInitial: [null],
      dateEnd: [null],
      cliente: [''],
      consultor: [''],
      fornecedorId: [null],
      tipoFornecedor: [null],
      semMotorista: [false],
      comOcorrencia: [false],
    })
  }

  getListFornecedores() {
    this.supplierService.listSupplier().subscribe({
      next: (res: any) => { this.listFornecedores = res.data ?? [] },
      error: (err: any) => { console.error('Ocorreu um erro ao listar os fornecedores', err) },
    })
  }

  fornecedorLabel(fornecedor: any): string {
    return fornecedor.nomeFantasia || fornecedor.razaoSocial || fornecedor.nome || '-'
  }

  addClient() {
    const dialogRef = this.dialog.open(ModalLegalEntityClientComponent, {
      enterAnimationDuration: '100ms',
      exitAnimationDuration: '100ms',
      maxWidth: '1200px',
      minWidth: '1200px',
      maxHeight: '650px',
      data: { item: this.clientSelected, multiSelect: false },
    })
    dialogRef.componentInstance.responseSingle.subscribe((response) => {
      if (response) {
        if (response.remove == false) {
          this.clientSelected = response.selection
          this.form.get('cliente')!.setValue(this.clientSelected.nomeFantasia)
        } else {
          this.clientSelected = {}
          this.form.get('cliente')!.setValue('')
        }
      }
    })
  }

  addConsultor() {
    const dialogRef = this.dialog.open(ModalEmployeeConsultantOperationalComponent, {
      enterAnimationDuration: '100ms',
      exitAnimationDuration: '100ms',
      maxWidth: '1200px',
      minWidth: '1200px',
      maxHeight: '650px',
      data: { item: this.consultorSelected, multiSelect: false },
    })
    dialogRef.componentInstance.responseSingle.subscribe((response: any) => {
      if (response) {
        if (response.remove == false) {
          this.consultorSelected = response.selection
          this.form.get('consultor')!.setValue(this.consultorSelected.nome)
        } else {
          this.consultorSelected = {}
          this.form.get('consultor')!.setValue('')
        }
      }
    })
  }

  createModel() {
    const formValue = this.form.value

    const dateInitialStr = formValue.dateInitial
      ? new Date(formValue.dateInitial).toLocaleDateString('pt-BR')
      : null
    const dateFinalStr = formValue.dateEnd
      ? new Date(formValue.dateEnd).toLocaleDateString('pt-BR')
      : null

    const model: any = {
      dataInicio: dateInitialStr,
      dataFinal: dateFinalStr,
    }

    if (this.clientSelected?.id) model.clienteId = this.clientSelected.id
    if (this.consultorSelected?.id) model.consultorOperacionalId = this.consultorSelected.id
    if (formValue.fornecedorId) model.fornecedorId = formValue.fornecedorId
    if (formValue.tipoFornecedor) model.tipoFornecedor = formValue.tipoFornecedor
    if (formValue.semMotorista) model.semMotorista = true
    if (formValue.comOcorrencia) model.comOcorrencia = true

    return model
  }

  // Emite o model pro back (mesmo shape de sempre — buildModel() da página o
  // espalha direto no corpo do search-paginated) junto com o raw pra reabrir este
  // modal já preenchido da próxima vez (ver ngOnInit/MAT_DIALOG_DATA).
  search() {
    this.response.emit({
      model: this.createModel(),
      raw: { form: this.form.getRawValue(), clientSelected: this.clientSelected, consultorSelected: this.consultorSelected },
    })
    this.dialogRef.close()
  }

  resetForm() {
    this.clientSelected = {}
    this.consultorSelected = {}
    this.createForm()
    this.response.emit('reset')
    this.dialogRef.close()
  }

  close(): void {
    this.dialogRef.close()
  }
}
